import UserProvider, { useUser } from './contexts/UserProvider'
import './styles/auth-only.css'
import { useEffect, useState } from 'react'

type Theme = 'light' | 'dark'

const THEME_STORAGE_KEY = 'theme'

const getInitialTheme = (): Theme => {
  const stored = window.localStorage.getItem(THEME_STORAGE_KEY)
  if (stored === 'light' || stored === 'dark') {
    return stored
  }

  return window.matchMedia('(prefers-color-scheme: dark)').matches
    ? 'dark'
    : 'light'
}

const AuthScreen = () => {
  const { status, userTg, userBackend, errorMessage, retry } = useUser()
  const [theme, setTheme] = useState<Theme>(() => getInitialTheme())

  useEffect(() => {
    const root = document.documentElement
    root.classList.toggle('dark', theme === 'dark')
    window.localStorage.setItem(THEME_STORAGE_KEY, theme)
  }, [theme])

  const toggleTheme = () =>
    setTheme((current) => (current === 'dark' ? 'light' : 'dark'))

  const themeButtonText =
    theme === 'dark' ? 'Switch to light theme' : 'Switch to dark theme'
  const themeToggle = (
    <button
      className='theme-toggle'
      onClick={toggleTheme}
      aria-label={themeButtonText}
      title={themeButtonText}
    >
      <span
        className={`theme-toggle__track ${theme === 'dark' ? 'is-dark' : ''}`}
      >
        <span className='theme-toggle__thumb' />
      </span>
      <span className='theme-toggle__label'>
        {theme === 'dark' ? 'Dark' : 'Light'}
      </span>
    </button>
  )

  if (status === 'loading') {
    return (
      <main className='auth-shell'>
        {themeToggle}
        <div className='auth-card'>
          <div className='spinner' />
          <p className='auth-text'>Initializing authentication...</p>
        </div>
      </main>
    )
  }

  if (status === 'error') {
    return (
      <main className='auth-shell'>
        {themeToggle}
        <div className='auth-card'>
          <h1 className='auth-title'>Authentication failed</h1>
          <p className='auth-text'>{errorMessage}</p>
          <button className='auth-button' onClick={() => void retry()}>
            Retry
          </button>
        </div>
      </main>
    )
  }

  return (
    <main className='auth-shell'>
      {themeToggle}
      <div className='auth-card'>
        <h1 className='auth-title'>Authenticated</h1>
        <p className='auth-text'>{`Telegram: ${userTg?.id ?? '-'}`}</p>
        <p className='auth-text'>{`Username: ${userBackend?.userName ?? '-'}`}</p>
      </div>
    </main>
  )
}

function App() {
  return (
    <UserProvider>
      <AuthScreen />
    </UserProvider>
  )
}

export default App
